import { useTranslation } from 'react-i18next';
import { cn } from '@c2fo/liquidity';
import { useCurrencyContext } from '@/contexts/CurrencyContext';
import { BuyerGroupWithStats } from '@/enterprise/data/useGroupByBuyer';
import { useMarketStatsStatus } from '@/lib/useMarketStatsStatus';
import handleNaN from '@/utils/handleNaN';
import { useLocaleFormat } from '@/utils/useLocaleFormat';

const CustomerStats = ({ buyer }: { buyer: BuyerGroupWithStats }) => {
  const { t } = useTranslation();
  const { selectedCurrency } = useCurrencyContext();
  const { listenToMarketStats } = useMarketStatsStatus();
  const { asCurrency, asDateStringText, asNumber } = useLocaleFormat();

  const listeningForMarketStat = listenToMarketStats(
    buyer.takerMarkets.map(({ marketUuid, takerDivisionId, takerDivisionUuid, makerOrganizationUuid }) => ({
      marketUuid,
      takerId: takerDivisionId,
      takerDivisionUuid,
      makerOrganizationUuid,
    }))
  );

  return (
    <div className="@container/customer-stats">
      <div className="grid grid-cols-1 gap-4 bg-secondary-500/10 p-4 md:grid-cols-2 lg:grid-cols-4">
        <div className="text-center">
          <div className={cn({ 'blur-sm': listeningForMarketStat })}>
            {asCurrency(buyer.stats.eligibleInvoiceAmount, selectedCurrency)}
          </div>
          <div className="text-sm text-text-secondary">{t('core.includedAmount')}</div>
        </div>
        <div className="text-center">
          <div className={cn({ 'blur-sm': listeningForMarketStat })}>
            {buyer.stats.nextGoodFundsDateWithEligibleInvoices
              ? asDateStringText({ date: buyer.stats.nextGoodFundsDateWithEligibleInvoices })
              : '--'}
          </div>
          <div className="text-sm text-text-secondary">{t('core.nextEstimatedDepositDate')}</div>
        </div>
        <div className="text-center">
          <div className={cn({ 'blur-sm': listeningForMarketStat })}>
            {asNumber({ value: buyer.stats.eligibleInvoiceCount, decimals: 0 })} /{' '}
            {asNumber({ value: buyer.stats.totalInvoiceCount, decimals: 0 })}
          </div>
          <div className="text-sm text-text-secondary">{t('core.invoicesIncluded')}</div>
        </div>
        <div className="text-center">
          <div className={cn({ 'blur-sm': listeningForMarketStat })}>
            {handleNaN(buyer.stats.avgDaysPaidEarly) > 0
              ? asNumber({
                  value: handleNaN(buyer.stats.avgDaysPaidEarly),
                  decimals: 0,
                })
              : '--'}
          </div>
          <div className="text-sm text-text-secondary">{t('core.averageDaysPaidEarly')}</div>
        </div>
      </div>
    </div>
  );
};

export default CustomerStats;
